import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage, publicAssetUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Building2,
    Phone,
    MapPin,
    Tag,
    ShieldCheck,
    ShieldAlert,
    LogOut,
    KeyRound,
    ImagePlus,
    Trash2,
} from "lucide-react";

export default function MerchantProfile() {
    const { user, logout, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const fileRef = useRef(null);
    useEffect(() => {
        api.get("/me/profile-summary").then((r) => setProfile(r.data)).catch(() => setProfile(null));
    }, []);
    const reloadProfile = async () => {
        try {
            const r = await api.get("/me/profile-summary");
            setProfile(r.data);
        } catch { /* noop */ }
    };
    const onLogoChosen = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 3 * 1024 * 1024) return toast.error("Logo must be under 3 MB");
        setUploadingLogo(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("purpose", "shop_logo");
            const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/me/shop-logo", { shop_logo_upload_id: up.data.id });
            await reloadProfile();
            await refresh();
            toast.success("Shop logo updated");
        } catch (err) {
            toast.error(errorMessage(err));
        } finally { setUploadingLogo(false); if (fileRef.current) fileRef.current.value = ""; }
    };
    const removeLogo = async () => {
        try {
            await api.delete("/me/shop-logo");
            await reloadProfile();
            await refresh();
            toast.success("Shop logo removed");
        } catch (err) {
            toast.error(errorMessage(err));
        }
    };
    if (!user) return null;

    const docs = user.verification_docs || {};
    const docsComplete = docs.shop_business_license && docs.pan_or_aadhaar;

    const statusTone =
        user.status === "ACTIVE"
            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
            : user.status === "PENDING_APPROVAL"
              ? "bg-orange-100 text-orange-700 border-orange-200"
              : "bg-rose-100 text-rose-700 border-rose-200";

    return (
        <MerchantLayout>
            <div className="px-4 pb-6 pt-4" data-testid="merchant-profile-page">
                <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl nest-gradient text-white nest-shadow text-xl font-bold">
                        {(user.business_name || user.full_name || "S").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="truncate text-lg font-extrabold text-neutral-900" data-testid="prof-business-name">
                            {user.business_name || user.full_name}
                        </div>
                        <div className="mt-0.5 text-xs text-neutral-500">
                            {user.full_name} · {user.mobile_number || "—"}
                        </div>
                        <Badge
                            variant="outline"
                            className={`mt-2 text-[10px] uppercase ${statusTone}`}
                            data-testid="prof-status"
                        >
                            {user.status.replace("_", " ")}
                        </Badge>
                    </div>
                </div>

                <Card className="mt-2 rounded-2xl border border-neutral-200 p-4" data-testid="prof-business-card">
                    <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Business
                    </div>
                    <div className="mt-2 space-y-2 text-sm">
                        <Row icon={<Building2 size={14} />} label="Type" value={user.business_type} />
                        <Row icon={<Tag size={14} />} label="Category" value={user.business_category} />
                        <Row icon={<MapPin size={14} />} label="District" value={user.business_district} />
                        <Row icon={<Phone size={14} />} label="Mobile" value={user.mobile_number} />
                    </div>
                </Card>

                {/* Phase 8 — Shop logo */}
                <Card className="mt-3 rounded-2xl border border-neutral-200 p-4" data-testid="prof-shop-logo-card">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        <ImagePlus size={14} /> Shop Logo
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-orange-200 bg-orange-50" data-testid="prof-logo-preview">
                            {profile?.shop_logo_url ? (
                                <img src={publicAssetUrl(profile.shop_logo_url)} alt="Shop logo" className="h-full w-full object-cover" />
                            ) : profile?.shop_photo_url ? (
                                <img src={publicAssetUrl(profile.shop_photo_url)} alt="Shop photo (fallback)" className="h-full w-full object-cover opacity-70" />
                            ) : (
                                <span className="text-3xl font-black text-orange-500">{(user.business_name || user.full_name || "S").charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs text-neutral-600">
                                Square, transparent PNG preferred. Used on posters + shop cards.
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                                <label className="cursor-pointer inline-flex items-center gap-1 rounded-md nest-gradient px-3 py-1.5 text-xs font-semibold text-white" data-testid="prof-logo-upload-label">
                                    <ImagePlus size={12} /> {profile?.shop_logo_url ? "Change" : "Upload"}
                                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploadingLogo} onChange={onLogoChosen} data-testid="prof-logo-upload-input" />
                                </label>
                                {profile?.shop_logo_url && (
                                    <Button variant="outline" size="sm" onClick={removeLogo} data-testid="prof-logo-remove"><Trash2 size={12} className="mr-1" /> Remove</Button>
                                )}
                            </div>
                            {!profile?.shop_logo_url && profile?.shop_photo_url && (
                                <div className="mt-2 text-[11px] italic text-neutral-500">Currently using your verification shop photo as a fallback.</div>
                            )}
                        </div>
                    </div>
                </Card>

                <Card className="mt-3 rounded-2xl border border-neutral-200 p-4" data-testid="prof-docs-card">
                    <div className="flex items-center gap-2">
                        {docsComplete ? (
                            <ShieldCheck className="text-emerald-600" size={16} />
                        ) : (
                            <ShieldAlert className="text-orange-600" size={16} />
                        )}
                        <div className="text-sm font-bold">Verification documents</div>
                    </div>
                    <ul className="mt-3 space-y-2 text-xs">
                        <DocRow label="Shop / Trade License" filled={!!docs.shop_business_license} />
                        <DocRow label="PAN or Aadhaar" filled={!!docs.pan_or_aadhaar} />
                        <DocRow label="Shop Photo / Offer Proof" filled={!!docs.shop_photo_or_offer_proof} optional />
                        <DocRow label="Address Proof" filled={!!docs.business_address_proof} optional />
                    </ul>
                </Card>

                <Card className="mt-3 rounded-2xl border border-neutral-200 p-4">
                    <div className="text-sm font-bold">Security</div>
                    <div className="mt-2">
                        <ChangePasswordDialog />
                    </div>
                </Card>

                <div className="mt-6">
                    <Button
                        variant="outline"
                        className="h-11 w-full"
                        onClick={logout}
                        data-testid="prof-logout-btn"
                    >
                        <LogOut size={14} className="mr-2" /> Sign out
                    </Button>
                </div>
            </div>
        </MerchantLayout>
    );
}

function Row({ icon, label, value }) {
    return (
        <div className="flex items-center gap-2 text-neutral-700">
            <span className="text-neutral-400">{icon}</span>
            <span className="min-w-[64px] text-neutral-500">{label}</span>
            <span className="font-medium text-neutral-800">{value || "—"}</span>
        </div>
    );
}

function DocRow({ label, filled, optional }) {
    return (
        <li className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-2">
            <span className="text-neutral-700">
                {label}
                {optional && (
                    <span className="ml-1 text-[10px] uppercase text-neutral-400">optional</span>
                )}
            </span>
            <Badge
                variant="outline"
                className={`text-[10px] ${filled ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-white text-neutral-500"}`}
            >
                {filled ? "Uploaded" : "Missing"}
            </Badge>
        </li>
    );
}

function ChangePasswordDialog() {
    const [open, setOpen] = useState(false);
    const [oldp, setOldP] = useState("");
    const [newp, setNewP] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!oldp || newp.length < 8) {
            toast.error("New password must be at least 8 characters.");
            return;
        }
        try {
            setBusy(true);
            await api.post("/auth/change-password", {
                old_password: oldp,
                new_password: newp,
            });
            toast.success("Password changed");
            setOldP("");
            setNewP("");
            setOpen(false);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="prof-change-password-btn">
                    <KeyRound size={14} className="mr-2" /> Change password
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[calc(100vw-32px)] rounded-2xl sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change password</DialogTitle>
                    <DialogDescription>
                        Enter your current password and choose a new one (min 8 characters).
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Current password</Label>
                        <Input
                            type="password"
                            value={oldp}
                            onChange={(e) => setOldP(e.target.value)}
                            data-testid="prof-cp-old"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">New password</Label>
                        <Input
                            type="password"
                            value={newp}
                            onChange={(e) => setNewP(e.target.value)}
                            data-testid="prof-cp-new"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        disabled={busy}
                        onClick={submit}
                        className="nest-gradient text-white"
                        data-testid="prof-cp-submit"
                    >
                        Update
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
